import { z } from "zod";
import { opaqueCursorSchema, opaqueIdSchema } from "./opaque.js";
export const memoryPrivacySchema = z.enum(["OWNER_ONLY", "BOOK_MEMBERS", "EXPLICIT_GRANTS"]);
export const memoryPrecisionSchema = z.enum(["EXACT", "APPROXIMATE", "PRIVATE"]);
export const indexedPersonSchema = z
    .object({
    personId: opaqueIdSchema,
    displayName: z.string().trim().min(1).max(120),
    relationshipLabel: z.string().trim().min(1).max(80).nullable(),
    privacy: memoryPrivacySchema,
    precision: memoryPrecisionSchema,
    chapterIds: z.array(opaqueIdSchema).max(250),
    archiveItemIds: z.array(opaqueIdSchema).max(250),
})
    .strict();
export const indexedPlaceSchema = z
    .object({
    placeId: opaqueIdSchema,
    displayName: z.string().trim().min(1).max(160),
    regionCode: z
        .string()
        .regex(/^[A-Z0-9-]{2,12}$/)
        .nullable(),
    countryCode: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable(),
    privacy: memoryPrivacySchema,
    precision: memoryPrecisionSchema,
    chapterIds: z.array(opaqueIdSchema).max(250),
    archiveItemIds: z.array(opaqueIdSchema).max(250),
})
    .strict();
export const memoryDatePrecisionSchema = z.enum(["DAY", "MONTH", "YEAR", "APPROXIMATE", "UNKNOWN"]);
export const memoryDateSchema = z
    .object({
    value: z
        .string()
        .regex(/^\d{4}(?:-\d{2}(?:-\d{2})?)?$/)
        .nullable(),
    precision: memoryDatePrecisionSchema,
})
    .strict()
    .superRefine((value, context) => {
    const patterns = {
        DAY: /^\d{4}-\d{2}-\d{2}$/,
        MONTH: /^\d{4}-\d{2}$/,
        YEAR: /^\d{4}$/,
        APPROXIMATE: /^\d{4}(?:-\d{2}(?:-\d{2})?)?$/,
    };
    if (value.precision === "UNKNOWN" && value.value !== null)
        context.addIssue({
            code: "custom",
            path: ["value"],
            message: "An unknown date cannot carry a date value.",
        });
    if (value.precision !== "UNKNOWN" &&
        (value.value === null || !patterns[value.precision].test(value.value)))
        context.addIssue({
            code: "custom",
            path: ["value"],
            message: "The value must match its declared date precision.",
        });
});
export const memoryEventSchema = z
    .object({
    eventId: opaqueIdSchema,
    displayTitle: z.string().trim().min(1).max(200),
    date: memoryDateSchema,
    personIds: z.array(opaqueIdSchema).max(250),
    placeIds: z.array(opaqueIdSchema).max(50),
    chapterIds: z.array(opaqueIdSchema).max(250),
    archiveItemIds: z.array(opaqueIdSchema).max(250),
    privacy: memoryPrivacySchema,
    precision: memoryPrecisionSchema,
})
    .strict();
export const songReferenceSchema = z
    .object({
    songReferenceId: opaqueIdSchema,
    title: z.string().trim().min(1).max(200),
    artist: z.string().trim().min(1).max(200),
    releaseYear: z.number().int().min(1800).max(2200).nullable(),
    externalCatalogCode: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9][a-z0-9_.:-]*$/i)
        .nullable(),
    privacy: memoryPrivacySchema,
})
    .strict();
export const memoryPlaylistSchema = z
    .object({
    playlistId: opaqueIdSchema,
    displayName: z.string().trim().min(1).max(160),
    songCount: z.number().int().nonnegative(),
    chapterIds: z.array(opaqueIdSchema).max(250),
    archiveItemIds: z.array(opaqueIdSchema).max(250),
    privacy: memoryPrivacySchema,
})
    .strict();
export const familyTreeRelationshipSchema = z.enum([
    "PARENT",
    "CHILD",
    "PARTNER",
    "SIBLING",
    "GUARDIAN",
    "RELATIVE",
]);
export const familyTreeEdgeSchema = z
    .object({
    edgeId: opaqueIdSchema,
    fromPersonId: opaqueIdSchema,
    toPersonId: opaqueIdSchema,
    relationship: familyTreeRelationshipSchema,
    privacy: memoryPrivacySchema,
    precision: memoryPrecisionSchema,
})
    .strict()
    .refine((edge) => edge.fromPersonId !== edge.toPersonId, {
    path: ["toPersonId"],
    message: "A family-tree edge cannot point to the same person.",
});
export const memoryIndexPageInfoSchema = z
    .object({ nextCursor: opaqueCursorSchema.nullable(), hasMore: z.boolean() })
    .strict()
    .superRefine((value, context) => {
    if (value.hasMore !== (value.nextCursor !== null))
        context.addIssue({
            code: "custom",
            path: ["nextCursor"],
            message: "nextCursor must be present exactly when hasMore is true.",
        });
});
export const memoryIndexPageQuerySchema = z
    .object({
    cursor: opaqueCursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .strict();
function pageOf(item) {
    return z.object({ items: z.array(item).max(100), page: memoryIndexPageInfoSchema }).strict();
}
export const indexedPersonPageSchema = pageOf(indexedPersonSchema);
export const indexedPlacePageSchema = pageOf(indexedPlaceSchema);
export const memoryEventPageSchema = pageOf(memoryEventSchema);
export const memoryPlaylistPageSchema = pageOf(memoryPlaylistSchema);
export const songReferencePageSchema = pageOf(songReferenceSchema);
export const familyTreeEdgePageSchema = pageOf(familyTreeEdgeSchema);
export const memoryIndexAnalyticsNameSchema = z.enum([
    "MEMORY_INDEX_OPENED",
    "TIMELINE_FILTERED",
    "EVENT_OPENED",
    "PERSON_FILTERED",
    "PLACE_FILTERED",
    "PLAYLIST_OPENED",
    "FAMILY_TREE_OPENED",
]);
export const memoryIndexEntityKindSchema = z.enum([
    "PERSON",
    "PLACE",
    "EVENT",
    "PLAYLIST",
    "FAMILY_TREE_EDGE",
]);
export const memoryIndexAnalyticsEventSchema = z
    .object({
    eventId: opaqueIdSchema,
    name: memoryIndexAnalyticsNameSchema,
    occurredAt: z.string().datetime(),
    bookId: opaqueIdSchema,
    entityId: opaqueIdSchema.nullable(),
    entityKind: memoryIndexEntityKindSchema.nullable(),
    resultCount: z.number().int().nonnegative().max(100_000).nullable(),
    precision: memoryPrecisionSchema.nullable(),
})
    .strict();
//# sourceMappingURL=memory-index.js.map